import { useLocalization } from "@fluent/react";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useApi } from "@lending/lib/api";
import { getMembershipShareEndpoint } from "@lending/pages/Common/MembershipShare/hooks/getMembershipShareEndpoint";
import type {
  MembershipRequirementResponse,
  MembershipSharePrimaryEmailRequest,
} from "@lending/types";

function useUpdateMembershipSharePrimaryEmail() {
  const { l10n } = useLocalization();
  const $api = useApi();
  const queryClient = useQueryClient();
  const { showErrorToast } = useToastsContext();
  const lendingApplicationUUID = useLendingApplicationID();
  const applicationType = useProductType();
  const endpoint = getMembershipShareEndpoint(applicationType, "membership_share_primary_email");
  const membershipShareAccessEndpoint = getMembershipShareEndpoint(
    applicationType,
    "membership_share_access"
  );

  const applicationQueryKeyByProductType = {
    "personal-loan": ["get", "/v2/lending/personal_loan_applications/{uuid}/"],
    "credit-card": ["get", "/v2/lending/credit_card_applications/{uuid}/"],
    "auto-loan": ["get", "/v2/lending/vehicle_loan_applications/{uuid}/"],
  } as const;

  const mutation = $api.useMutation("post", endpoint, {
    onSuccess: async () => {
      if (!applicationType) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: applicationQueryKeyByProductType[applicationType],
      });
      await queryClient.invalidateQueries({
        queryKey: ["get", membershipShareAccessEndpoint],
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        extra: { lendingApplicationUUID, applicationType },
      });
      showErrorToast(
        l10n.getString(
          "error-email-update",
          null,
          "There was an error updating your email. Please try again."
        )
      );
    },
  });

  const updateMembershipSharePrimaryEmail = async (
    payload: MembershipSharePrimaryEmailRequest
  ): Promise<MembershipRequirementResponse> => {
    if (!lendingApplicationUUID) {
      throw new Error("Membership share primary email endpoint is not available");
    }

    return mutation.mutateAsync({
      params: { path: { uuid: lendingApplicationUUID } },
      body: payload,
    });
  };

  return {
    updateMembershipSharePrimaryEmail,
    isPending: mutation.isPending,
  };
}

export { useUpdateMembershipSharePrimaryEmail };
